import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  AppBar,
  Toolbar,
  Alert,
  Paper,
  Grid,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import SearchBar from './components/SearchBar';
import DocumentList from './components/DocumentList';
import LoadingSpinner from './components/LoadingSpinner';
import LanguageToggle from './components/LanguageToggle';
import { useDocumentSearch } from './hooks/useDocumentSearch';
import { useLanguage } from './contexts/LanguageContext';
import { useThemeContext } from './contexts/ThemeContext';
import { loadAppConfig } from './services/configService';
import { initializeConfig } from './config/config';

function App() {
  const { t, isLoaded: translationsLoaded } = useLanguage();
  const { isLoaded: themeLoaded } = useThemeContext();
  const {
    documents,
    isLoading,
    isLoadingMore,
    error,
    searchPerformed,
    productInfo,
    search,
    download,
  } = useDocumentSearch();
  const [appLogo, setAppLogo] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);

  // Load configuration from API on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await loadAppConfig();
        // Initialize the global config with API settings
        if (config) {
          initializeConfig(config);
        }
        if (config && config.logo) {
          setAppLogo(config.logo);
        }
      } catch (error) {
        console.error('Failed to load app config:', error);
      } finally {
        setConfigLoaded(true);
      }
    };
    loadConfig();
  }, []);

  // Wait for all configurations to load before rendering the app
  if (!configLoaded || !translationsLoaded || !themeLoaded) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <LoadingSpinner message="Loading configuration..." />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar 
        position="static" 
        elevation={0}
        sx={(theme) => ({ 
          bgcolor: theme.custom?.topBar?.background || 'primary.main',
          color: theme.custom?.topBar?.text || 'primary.contrastText'
        })}
      >
        <Toolbar sx={{ position: 'relative' }}>
          {appLogo ? (
            <Box
              component="img"
              src={appLogo}
              alt="Logo"
              sx={{ height: 40, mr: 2 }}
            />
          ) : (
            <DescriptionIcon sx={(theme) => ({ mr: 2, color: theme.custom?.topBar?.text || 'primary.contrastText' })} />
          )}
          <Typography 
            variant="h6" 
            component="h1" 
            sx={(theme) => ({ 
              color: theme.custom?.topBar?.text || 'primary.contrastText', 
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
            })}
          >
            {t('appTitle')}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <LanguageToggle />
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ flexGrow: 1, py: 4 }}>
        {/* Hero Section */}
        <Box
          sx={{
            textAlign: 'center',
            mb: 4,
            py: { xs: 2, md: 4 },
          }}
        >
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 300,
              fontSize: { xs: '2rem', md: '3rem' },
            }}
          >
            {t('heroTitle')}
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: 'auto' }}
          >
            {t('heroSubtitle')}
          </Typography>
        </Box>

        {/* Search Section */}
        <Box>
          <SearchBar onSearch={search} disabled={isLoading} />

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
              {error}
            </Alert>
          )}

          {/* Product Information */}
          {productInfo && !error && (
            <Paper sx={{ p: 2, mb: 3, maxWidth: 600, mx: 'auto' }}>
              <Typography variant="h6" gutterBottom>
                {t('productInfoTitle')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('productInfoItemCode')}
                  </Typography>
                  <Typography variant="body1">{productInfo.codiceArticolo || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('productInfoSerialNumber')}
                  </Typography>
                  <Typography variant="body1">{productInfo.lotto || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('productInfoOrder')}
                  </Typography>
                  <Typography variant="body1">{productInfo.commessa || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('productInfoYourReference')}
                  </Typography>
                  <Typography variant="body1">{productInfo.numDocOriginale || '-'}</Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Loading State - only show full loader when no results yet */}
          {isLoading && !productInfo && documents.length === 0 && <LoadingSpinner message={t('loadingMessage')} />}

          {/* Keep documents loading visible even after product info is shown */}
          {isLoadingMore && (productInfo || documents.length > 0) && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <LoadingSpinner message={t('loadingMoreDocuments')} />
            </Box>
          )}

          {/* Results - show immediately when available, even while loading more */}
          {documents.length > 0 && (
            <>
              <DocumentList
                documents={documents}
                onDownload={download}
                showEmpty={searchPerformed}
              />
            </>
          )}

          {/* No results message - only when search done and no docs */}
          {!isLoading && !isLoadingMore && documents.length === 0 && searchPerformed && (
            <DocumentList
              documents={[]}
              onDownload={download}
              showEmpty={true}
            />
          )}
        </Box>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={(theme) => ({
          py: 2,
          px: 2,
          mt: 'auto',
          bgcolor: theme.custom?.footer?.background || 'background.paper',
          textAlign: 'center',
        })}
      >
        <Typography 
          variant="body2" 
          sx={(theme) => ({ color: theme.custom?.footer?.text || 'text.secondary' })}
        >
          © 2026 {t('footerText')}{' '}
          <a
            href="https://www.phyosrl.it"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: 'inherit',
              textDecoration: 'none',
              fontWeight: 'bold',
            }}
          >
            {t('companyName')}
          </a>
        </Typography>
      </Box>
    </Box>
  );
}

export default App;
